import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, effect, inject, Injector, input, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressBar } from '@angular/material/progress-bar';
import * as Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { isEmpty, orderBy } from 'lodash';
import moment from 'moment';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ChartAxisPoint } from '../../../models/chart-axis-point.model';
import { ChartService } from '../../../services/chart.service';
import { ChartFilterComponent } from '../chart-filter/chart-filter.component';

@Component({
  selector: 'oyan-ui-chart-rssi-cylinder',
  templateUrl: './chart-rssi-cylinder.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HighchartsChartComponent, MatProgressBar, ChartFilterComponent]
})
export class ChartRssiCylinderComponent implements OnDestroy {
  id = input<string>();

  readonly CHART_PERIOD_CONSTANTS = {
    DEFAULT_DAYS: 14,
    CHART_COLOR: '#375F9B'
  } as const;
  readonly CHART_STYLE_CONSTANTS = {
    FONT_FAMILY: '"Lucida Grande", "Lucida Sans Unicode", Arial, Helvetica, sans-serif',
    FONT_SIZE: '12px'
  } as const;

  displayLoader = signal(false);
  dataRange = signal<{ start: string; end: string }>({
    start: '',
    end: ''
  });
  chartOptions = signal<Highcharts.Options>(this.createInitialChartOptions());
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private chart: Highcharts.Chart | null = null;
  private readonly chartService = inject(ChartService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);

  constructor() {
    effect(
      () => {
        const dataRange = this.dataRange();
        const id = this.id();
        if (id && !isEmpty(dataRange?.start) && !isEmpty(dataRange?.end)) {
          this.displayLoader.set(true);
          this.chartService
            .getCylinderRssiPoints(id, moment(dataRange.start), moment(dataRange.end))
            .pipe(
              catchError(() => of([])),
              finalize(() => this.displayLoader.set(false)),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((points: ChartAxisPoint[]) => {
              this.renderGraph(points);
            });
        }
      },
      { injector: this.injector }
    );
  }

  onChartCreated(chart: Highcharts.Chart): void {
    this.chart = chart;
  }

  dataRangeChange(event: { start: string; end: string }): void {
    this.dataRange.set(event);
  }

  ngOnDestroy(): void {
    this.displayLoader.set(false);
    this.chart = null;
  }

  private createInitialChartOptions(): Highcharts.Options {
    return {
      colors: [this.CHART_PERIOD_CONSTANTS.CHART_COLOR],
      title: { text: '' },
      credits: { enabled: false },
      navigator: { enabled: false },
      rangeSelector: {
        enabled: false,
        inputEnabled: false
      },
      tooltip: {
        shared: true,
        split: false
      },
      scrollbar: { enabled: false },
      chart: {
        style: {
          fontFamily: this.CHART_STYLE_CONSTANTS.FONT_FAMILY,
          fontSize: this.CHART_STYLE_CONSTANTS.FONT_SIZE
        },
        zooming: {
          type: 'x',
          singleTouch: true
        }
      },
      yAxis: {
        title: { text: 'RSSI' },
        labels: {
          format: '{value}',
          style: {
            color: this.CHART_PERIOD_CONSTANTS.CHART_COLOR
          }
        }
      },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          millisecond: '%H:%M:%S.%L',
          second: '%H:%M:%S',
          minute: '%H:%M',
          hour: '%H:%M',
          day: '%e. %b %y',
          week: '%e. %b %y',
          month: '%b %y',
          year: '%Y'
        }
      },
      legend: { enabled: false },
      series: [],
      exporting: {
        buttons: {
          contextButton: {
            menuItems: ['viewFullscreen', 'separator', 'downloadCSV', 'downloadXLS']
          }
        }
      }
    };
  }

  private reflow(): void {
    this.chart?.reflow();
  }

  private renderGraph(points: ChartAxisPoint[]): void {
    const sortedSeries = orderBy(points, ['x'], ['asc']);
    const series = [
      {
        type: 'line',
        data: sortedSeries,
        tooltip: {
          pointFormatter(): string {
            return `${this.y}`;
          }
        }
      }
    ] as Highcharts.SeriesLineOptions[];

    this.chartOptions.update((chartOptions) => ({
      ...chartOptions,
      series
    }));

    this.reflow();
    this.cdr.markForCheck();
  }
}
